import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  RssFeed as FeedIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

interface FeedItem {
  id: string;
  type: 'payment' | 'user' | 'entity' | 'invoice' | 'config' | 'sync';
  action: string;
  description: string;
  user: string;
  timestamp: string;
}

// Mock data
const mockFeedItems: FeedItem[] = [
  {
    id: '1',
    type: 'payment',
    action: 'Pagamento Criado',
    description: 'Novo pagamento de 15.000 CVE registado',
    user: 'João Silva',
    timestamp: '2026-01-09 11:45:00',
  },
  {
    id: '2',
    type: 'invoice',
    action: 'Fatura Emitida',
    description: 'Fatura FAT2026/000245 emitida para CV Telecom',
    user: 'Maria Santos',
    timestamp: '2026-01-09 11:30:00',
  },
  {
    id: '3',
    type: 'user',
    action: 'Utilizador Criado',
    description: 'Novo utilizador "Carlos Mendes" registado no sistema',
    user: 'Admin',
    timestamp: '2026-01-09 11:15:00',
  },
  {
    id: '4',
    type: 'sync',
    action: 'Sincronização SISP',
    description: 'Entidade 10100 sincronizada com sucesso',
    user: 'Sistema',
    timestamp: '2026-01-09 11:00:00',
  },
  {
    id: '5',
    type: 'payment',
    action: 'Pagamento Confirmado',
    description: 'Pagamento REF789012 confirmado pela entidade',
    user: 'Sistema',
    timestamp: '2026-01-09 10:45:00',
  },
  {
    id: '6',
    type: 'entity',
    action: 'Entidade Actualizada',
    description: 'Dados da entidade "Electra" actualizados',
    user: 'Pedro Costa',
    timestamp: '2026-01-09 10:30:00',
  },
  {
    id: '7',
    type: 'config',
    action: 'Configuração Alterada',
    description: 'Taxa IVA actualizada de 15% para 16%',
    user: 'Admin',
    timestamp: '2026-01-09 10:15:00',
  },
  {
    id: '8',
    type: 'invoice',
    action: 'Fatura Paga',
    description: 'Fatura FAT2026/000234 marcada como paga',
    user: 'Ana Rodrigues',
    timestamp: '2026-01-09 10:00:00',
  },
  {
    id: '9',
    type: 'payment',
    action: 'Pagamento Cancelado',
    description: 'Pagamento REF456789 cancelado pelo utilizador',
    user: 'João Silva',
    timestamp: '2026-01-09 09:45:00',
  },
  {
    id: '10',
    type: 'user',
    action: 'Perfil Actualizado',
    description: 'Utilizador "Maria Santos" actualizou o perfil',
    user: 'Maria Santos',
    timestamp: '2026-01-09 09:30:00',
  },
  {
    id: '11',
    type: 'sync',
    action: 'Sincronização Automática',
    description: 'Backup diário executado com sucesso',
    user: 'Sistema',
    timestamp: '2026-01-09 02:00:00',
  },
  {
    id: '12',
    type: 'entity',
    action: 'Nova Entidade',
    description: 'Entidade "Unitel T+" adicionada ao sistema',
    user: 'Admin',
    timestamp: '2026-01-08 16:20:00',
  },
  {
    id: '13',
    type: 'config',
    action: 'Serviço Activado',
    description: 'Serviço "Pagamentos MB WAY" activado',
    user: 'Admin',
    timestamp: '2026-01-08 15:00:00',
  },
  {
    id: '14',
    type: 'invoice',
    action: 'Fatura Vencida',
    description: 'Fatura FAT2025/001234 está vencida',
    user: 'Sistema',
    timestamp: '2026-01-08 00:00:00',
  },
  {
    id: '15',
    type: 'payment',
    action: 'Lote de Pagamentos',
    description: '25 pagamentos processados em lote',
    user: 'Sistema',
    timestamp: '2026-01-07 18:00:00',
  },
];

const Feeds: React.FC = () => {
  const [feedItems] = useState<FeedItem[]>(mockFeedItems);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'payment':
        return <PaymentIcon />;
      case 'user':
        return <PersonIcon />;
      case 'entity':
        return <BusinessIcon />;
      case 'invoice':
        return <ReceiptIcon />;
      case 'config':
        return <SettingsIcon />;
      case 'sync':
        return <SyncIcon />;
      default:
        return <FeedIcon />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'payment':
        return 'primary.main';
      case 'user':
        return 'info.main';
      case 'entity':
        return 'secondary.main';
      case 'invoice':
        return 'success.main';
      case 'config':
        return 'warning.main';
      case 'sync':
        return 'grey.600';
      default:
        return 'grey.400';
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      payment: 'Pagamento',
      user: 'Utilizador',
      entity: 'Entidade',
      invoice: 'Fatura',
      config: 'Configuração',
      sync: 'Sincronização',
    };
    return labels[type] || type;
  };

  const filteredItems = feedItems.filter(item => {
    const matchesSearch = searchTerm === '' ||
      item.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.user.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || item.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const stats = {
    total: feedItems.length,
    payment: feedItems.filter(i => i.type === 'payment').length,
    invoice: feedItems.filter(i => i.type === 'invoice').length,
    user: feedItems.filter(i => i.type === 'user').length,
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Feed de Actividade
        </Typography>
        <Button variant="contained" startIcon={<RefreshIcon />}>
          Actualizar
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FeedIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Actividades</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaymentIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.payment}</Typography>
                  <Typography variant="caption" color="text.secondary">Pagamentos</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReceiptIcon color="info" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.invoice}</Typography>
                  <Typography variant="caption" color="text.secondary">Faturas</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="secondary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.user}</Typography>
                  <Typography variant="caption" color="text.secondary">Utilizadores</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Pesquisar actividades..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Tipo"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="payment">Pagamentos</MenuItem>
                <MenuItem value="invoice">Faturas</MenuItem>
                <MenuItem value="user">Utilizadores</MenuItem>
                <MenuItem value="entity">Entidades</MenuItem>
                <MenuItem value="config">Configurações</MenuItem>
                <MenuItem value="sync">Sincronizações</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Feed List */}
      <Paper>
        <List sx={{ p: 0 }}>
          {filteredItems.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="Nenhuma actividade encontrada"
                secondary="Não há actividades que correspondam aos filtros seleccionados"
                sx={{ textAlign: 'center', py: 4 }}
              />
            </ListItem>
          ) : (
            filteredItems.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: getTypeColor(item.type) }}>
                      {getTypeIcon(item.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" fontWeight={500}>
                          {item.action}
                        </Typography>
                        <Chip size="small" label={getTypeLabel(item.type)} variant="outlined" />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {item.description}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            <PersonIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                            {item.user}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.timestamp}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < filteredItems.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}
        </List>
      </Paper>
    </Box>
  );
};

export default Feeds;
